/** One number with its word underneath.
 *
 * The design uses this shape at four sizes and they mean different things:
 * 34px is the headline of a dashboard card, 26–28px a lap time or a live
 * speed, 22px a figure in a header row, 20px a sector. The caption is always
 * 11px neutral, always below, always lower case — the number is the content
 * and the word is only there to say which number it is.
 *
 * Figures are tabular without being asked, because these sit in rows and a
 * proportional `1` would make two tiles of the same width disagree.
 */

import type { CSSProperties, ReactNode } from 'react';

export type StatSize = 'sm' | 'md' | 'lg' | 'xl';

export interface StatTileProps {
  value: ReactNode;
  label: ReactNode;
  size?: StatSize;
  /** `accent` for a best lap or a live figure, `muted` for a number that is
   *  context rather than news. */
  tone?: 'default' | 'accent' | 'muted';
  /** A second line under the caption — "lap 7", "of 121 unsurveyed". */
  sub?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  style?: CSSProperties;
}

const SIZE: Record<StatSize, number> = { sm: 20, md: 22, lg: 26, xl: 34 };

const TONE: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'var(--color-text)',
  accent: 'var(--color-accent-300)',
  muted: 'var(--color-neutral-500)',
};

export function StatTile({
  value,
  label,
  size = 'md',
  tone = 'default',
  sub,
  align = 'left',
  className,
  style,
}: StatTileProps) {
  return (
    <div className={className} style={{ textAlign: align, minWidth: 0, ...style }}>
      <div
        className="tabular-nums"
        style={{ fontSize: SIZE[size], lineHeight: 1, color: TONE[tone] }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-neutral-500)', marginTop: 5 }}>{label}</div>
      {sub ? (
        <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }} className="tabular-nums">
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export default StatTile;
